import { DataTableColumnDef } from 'components/data-table';
import { TypeCurveWellRow } from './types';
import { capitalize } from 'lodash-es';
import style from './type-curve-wells-table.module.scss';
import { format0, format1, format2 } from 'utils/number';
import { selfPairName } from 'components/well-page-content/utils';
import { formatCellValue } from 'components/data-table/utils';

// A well spaced against its own other laterals names itself as its neighbour. A missing
// neighbour renders the way the table's own text cells render it.
const neighborUwi = (uwi: string | null, rowUwi: string) => {
  if (uwi && uwi === rowUwi) return selfPairName(uwi);
  return formatCellValue(uwi, { type: 'text' });
};

// The export reads the row rather than the cell, so it names the self pair the same way.
const exportNeighborUwi = (uwi: string | null | undefined, rowUwi: string) => {
  if (uwi && uwi === rowUwi) return selfPairName(uwi);
  return uwi;
};

export const typeCurveWellsTableColumns: DataTableColumnDef<TypeCurveWellRow>[] =
  [
    {
      accessorKey: 'status',
      id: 'status',
      header: 'Well Status',
      size: 120,
      cell: (info) => capitalize(info.getValue() as string) || '--',
      meta: { type: 'text' },
    },
    {
      accessorKey: 'wellQualifier',
      id: 'wellQualifier',
      header: 'Well Qualifier',
      size: 150,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'uwi',
      id: 'uwi',
      header: 'UWI',
      size: 150,
      cell: (info) => {
        const uwi = info.getValue() as string;
        const href = info.row.original.href;
        if (uwi && href) {
          return (
            <a
              href={href}
              className={style.wellLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {uwi}
            </a>
          );
        }
        return uwi || '--';
      },
      meta: { type: 'text' },
    },
    {
      accessorKey: 'name',
      id: 'name',
      header: 'Well Name',
      size: 200,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'leaseName',
      id: 'leaseName',
      header: 'Lease Name',
      size: 180,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'wellNumber',
      id: 'wellNumber',
      header: 'Well Number',
      size: 120,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'holeDirection',
      id: 'holeDirection',
      header: 'Hole Direction',
      size: 140,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'currentOperator',
      id: 'currentOperator',
      header: 'Current Operator',
      size: 200,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'permitDate',
      id: 'permitDate',
      header: 'Permit Date',
      size: 120,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'spudDate',
      id: 'spudDate',
      header: 'Spud Date',
      size: 120,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'completionDate',
      id: 'completionDate',
      header: 'Completion Date',
      size: 140,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'firstProductionDate',
      id: 'firstProductionDate',
      header: 'First Production Date',
      size: 160,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'landingZone',
      id: 'landingZone',
      header: 'Landing Zone',
      size: 150,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'distanceToTopFt',
      id: 'distanceToTopFt',
      header: 'Distance to Top (ft)',
      size: 180,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'formation',
      id: 'formation',
      header: 'Formation',
      size: 150,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'trueVerticalDepthFeet',
      id: 'trueVerticalDepthFeet',
      header: 'True Vertical Depth (ft)',
      size: 160,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'integer' },
    },
    // Source
    {
      accessorKey: 'businessId',
      id: 'businessId',
      header: 'Volume Source',
      size: 260,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'sourceId',
      id: 'sourceId',
      header: 'Source ID',
      size: 150,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'generatedOn',
      id: 'generatedOn',
      header: 'Generated On',
      size: 140,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'perforatedLateralLengthFt',
      id: 'perforatedLateralLengthFt',
      header: 'Perf Lateral Length (ft)',
      size: 180,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'integer' },
    },
    // EUR per ft
    {
      accessorKey: 'oilEurPerFtBblFt',
      id: 'oilEurPerFtBblFt',
      header: 'Oil EUR (bbl/ft)',
      size: 160,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'gasEurPerFtMcfFt',
      id: 'gasEurPerFtMcfFt',
      header: 'Gas EUR (Mcf/ft)',
      size: 160,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'waterEurPerFtBblFt',
      id: 'waterEurPerFtBblFt',
      header: 'Water EUR (bbl/ft)',
      size: 170,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    // EUR
    {
      accessorKey: 'oilEurBbl',
      id: 'oilEurBbl',
      header: 'Oil EUR (bbl)',
      size: 140,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'gasEurMcf',
      id: 'gasEurMcf',
      header: 'Gas EUR (Mcf)',
      size: 140,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'waterEurBbl',
      id: 'waterEurBbl',
      header: 'Water EUR (bbl)',
      size: 150,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },

    // Scaled EUR
    {
      accessorKey: 'scaledOilEurBbl',
      id: 'scaledOilEurBbl',
      header: 'Scaled Oil EUR (bbl)',
      size: 170,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'scaledGasEurMcf',
      id: 'scaledGasEurMcf',
      header: 'Scaled Gas EUR (Mcf)',
      size: 170,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'scaledWaterEurBbl',
      id: 'scaledWaterEurBbl',
      header: 'Scaled Water EUR (bbl)',
      size: 180,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },

    // Ratios
    {
      accessorKey: 'gorMcfBbl',
      id: 'gorMcfBbl',
      header: 'GOR (Mcf/bbl)',
      size: 130,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'worBblBbl',
      id: 'worBblBbl',
      header: 'WOR (bbl/bbl)',
      size: 130,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'cgrBblMmcf',
      id: 'cgrBblMmcf',
      header: 'CGR (bbl/MMcf)',
      size: 140,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'wgrBblMmcf',
      id: 'wgrBblMmcf',
      header: 'WGR (bbl/MMcf)',
      size: 140,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'watercutPct',
      id: 'watercutPct',
      header: 'Watercut (%)',
      size: 120,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    // Stimulation
    {
      accessorFn: (row) => row.latestWellStimulationDetails?.jobDate ?? null,
      id: 'jobDate',
      header: 'Job Date',
      size: 120,
      meta: { type: 'date' },
    },
    {
      accessorKey: 'proppantLbsPerFt',
      id: 'proppantLbsPerFt',
      header: 'Proppant Intensity (lbs/ft)',
      size: 160,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 1 } },
    },
    {
      accessorKey: 'fluidGalPerFt',
      id: 'fluidGalPerFt',
      header: 'Fluid Intensity (gal/ft)',
      size: 140,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 1 } },
    },
    {
      accessorKey: 'fluidBblPerFt',
      id: 'fluidBblPerFt',
      header: 'Fluid Intensity (bbl/ft)',
      size: 140,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 1 } },
    },
    {
      accessorKey: 'waterGalPerFt',
      id: 'waterGalPerFt',
      header: 'Water Intensity (gal/ft)',
      size: 140,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 1 } },
    },
    {
      accessorKey: 'waterBblPerFt',
      id: 'waterBblPerFt',
      header: 'Water Intensity (bbl/ft)',
      size: 140,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 2 } },
    },
    {
      accessorKey: 'acidGalPerFt',
      id: 'acidGalPerFt',
      header: 'Acid Intensity (gal/ft)',
      size: 140,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 1 } },
    },
    {
      accessorKey: 'acidBblPerFt',
      id: 'acidBblPerFt',
      header: 'Acid Intensity (bbl/ft)',
      size: 140,
      cell: (info) => format2(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 2 } },
    },
    {
      accessorKey: 'stageSpacingFt',
      id: 'stageSpacingFt',
      header: 'Stage Spacing (ft)',
      size: 150,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left', format: { maxDecimalPlaces: 0 } },
    },
    {
      accessorKey: 'proppantMeshSize',
      id: 'proppantMeshSize',
      header: 'Proppant Mesh Size',
      size: 160,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'fluidSystem',
      id: 'fluidSystem',
      header: 'Fluid System',
      size: 150,
      meta: { type: 'text' },
    },
    // Well Spacing
    {
      accessorKey: 'closestInZoneUWI',
      id: 'closestInZoneUWI',
      header: 'Closest Neighbor In Zone UWI',
      size: 220,
      cell: (info) =>
        neighborUwi(info.getValue() as string | null, info.row.original.uwi),
      meta: {
        type: 'text',
        getExportValue: (row: TypeCurveWellRow) =>
          exportNeighborUwi(row.closestInZoneUWI, row.uwi),
      },
    },
    {
      accessorKey: 'closestInZoneTrueDistanceFt',
      id: 'closestInZoneTrueDistanceFt',
      header: 'Closest Neighbor In Zone True Dist (ft)',
      size: 260,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestInZoneHzDistanceFt',
      id: 'closestInZoneHzDistanceFt',
      header: 'Closest Neighbor In Zone Hz Dist (ft)',
      size: 250,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestInZoneVtDistanceFt',
      id: 'closestInZoneVtDistanceFt',
      header: 'Closest Neighbor In Zone Vt Dist (ft)',
      size: 250,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestInZoneOverlapPct',
      id: 'closestInZoneOverlapPct',
      header: 'Closest Neighbor In Zone Overlap (%)',
      size: 250,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestAnyZoneUWI',
      id: 'closestAnyZoneUWI',
      header: 'Closest Neighbor Any Zone UWI',
      size: 230,
      cell: (info) =>
        neighborUwi(info.getValue() as string | null, info.row.original.uwi),
      meta: {
        type: 'text',
        getExportValue: (row: TypeCurveWellRow) =>
          exportNeighborUwi(row.closestAnyZoneUWI, row.uwi),
      },
    },
    {
      accessorKey: 'closestAnyZoneTrueDistanceFt',
      id: 'closestAnyZoneTrueDistanceFt',
      header: 'Closest Neighbor Any Zone True Dist (ft)',
      size: 260,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestAnyZoneHzDistanceFt',
      id: 'closestAnyZoneHzDistanceFt',
      header: 'Closest Neighbor Any Zone Hz Dist (ft)',
      size: 260,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestAnyZoneVtDistanceFt',
      id: 'closestAnyZoneVtDistanceFt',
      header: 'Closest Neighbor Any Zone Vt Dist (ft)',
      size: 260,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'closestAnyZoneOverlapPct',
      id: 'closestAnyZoneOverlapPct',
      header: 'Closest Neighbor Any Zone Overlap (%)',
      size: 260,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestInZoneUWI',
      id: 'secondClosestInZoneUWI',
      header: '2nd Closest Neighbor In Zone UWI',
      size: 240,
      cell: (info) =>
        neighborUwi(info.getValue() as string | null, info.row.original.uwi),
      meta: {
        type: 'text',
        getExportValue: (row: TypeCurveWellRow) =>
          exportNeighborUwi(row.secondClosestInZoneUWI, row.uwi),
      },
    },
    {
      accessorKey: 'secondClosestInZoneTrueDistanceFt',
      id: 'secondClosestInZoneTrueDistanceFt',
      header: '2nd Closest Neighbor In Zone True Dist (ft)',
      size: 280,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestInZoneHzDistanceFt',
      id: 'secondClosestInZoneHzDistanceFt',
      header: '2nd Closest Neighbor In Zone Hz Dist (ft)',
      size: 270,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestInZoneVtDistanceFt',
      id: 'secondClosestInZoneVtDistanceFt',
      header: '2nd Closest Neighbor In Zone Vt Dist (ft)',
      size: 270,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestInZoneOverlapPct',
      id: 'secondClosestInZoneOverlapPct',
      header: '2nd Closest Neighbor In Zone Overlap (%)',
      size: 270,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestAnyZoneUWI',
      id: 'secondClosestAnyZoneUWI',
      header: '2nd Closest Neighbor Any Zone UWI',
      size: 250,
      cell: (info) =>
        neighborUwi(info.getValue() as string | null, info.row.original.uwi),
      meta: {
        type: 'text',
        getExportValue: (row: TypeCurveWellRow) =>
          exportNeighborUwi(row.secondClosestAnyZoneUWI, row.uwi),
      },
    },
    {
      accessorKey: 'secondClosestAnyZoneTrueDistanceFt',
      id: 'secondClosestAnyZoneTrueDistanceFt',
      header: '2nd Closest Neighbor Any Zone True Dist (ft)',
      size: 280,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestAnyZoneHzDistanceFt',
      id: 'secondClosestAnyZoneHzDistanceFt',
      header: '2nd Closest Neighbor Any Zone Hz Dist (ft)',
      size: 280,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestAnyZoneVtDistanceFt',
      id: 'secondClosestAnyZoneVtDistanceFt',
      header: '2nd Closest Neighbor Any Zone Vt Dist (ft)',
      size: 280,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'secondClosestAnyZoneOverlapPct',
      id: 'secondClosestAnyZoneOverlapPct',
      header: '2nd Closest Neighbor Any Zone Overlap (%)',
      size: 280,
      cell: (info) => format1(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgInZoneTrueDistanceFt',
      id: 'avgInZoneTrueDistanceFt',
      header: 'Avg Spacing In Zone True Dist (ft)',
      size: 240,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgInZoneHzDistanceFt',
      id: 'avgInZoneHzDistanceFt',
      header: 'Avg Spacing In Zone Hz Dist (ft)',
      size: 230,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgInZoneVtDistanceFt',
      id: 'avgInZoneVtDistanceFt',
      header: 'Avg Spacing In Zone Vt Dist (ft)',
      size: 230,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgAnyZoneTrueDistanceFt',
      id: 'avgAnyZoneTrueDistanceFt',
      header: 'Avg Spacing Any Zone True Dist (ft)',
      size: 250,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgAnyZoneHzDistanceFt',
      id: 'avgAnyZoneHzDistanceFt',
      header: 'Avg Spacing Any Zone Hz Dist (ft)',
      size: 240,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'avgAnyZoneVtDistanceFt',
      id: 'avgAnyZoneVtDistanceFt',
      header: 'Avg Spacing Any Zone Vt Dist (ft)',
      size: 240,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'decimal', align: 'left' },
    },
    {
      accessorKey: 'parentChildStatus',
      id: 'parentChildStatus',
      header: 'Parent/Child Status',
      size: 160,
      meta: { type: 'text' },
    },
    {
      accessorKey: 'numberOfParents',
      id: 'numberOfParents',
      header: 'Number of Parents',
      size: 160,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'integer' },
    },
    {
      accessorKey: 'numberOfChildren',
      id: 'numberOfChildren',
      header: 'Number of Children',
      size: 160,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'integer' },
    },
    {
      accessorKey: 'numberOfSiblings',
      id: 'numberOfSiblings',
      header: 'Number of Siblings',
      size: 160,
      cell: (info) => format0(info.getValue() as number),
      meta: { type: 'integer' },
    },
  ];
